import type { BillableFormPluginProps } from '@domains/subscription/priv/Billables/usecases/BillableForm/plugin'
import type { FieldChange } from '@domains/subscription/priv/components'
import { getChangedFormFields, normalizeFormData } from '@domains/subscription/priv/components/Confirmable/confirmable'
import { Stack, TextField, Typography } from '@mui/material'
import { memo } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import type { LinearPrice } from './linear'

export interface BillableFormProps extends BillableFormPluginProps {
  linearPrice: LinearPrice | undefined
  onSubmit: (data: LinearPrice) => Promise<void>
  unit: string
}

export const LinearPriceRow = memo(({ linearPrice, onRegisterSave, onSubmit, onDataChange, unit }: BillableFormProps) => {
  const form = useForm<LinearPrice>({
    values: linearPrice,
  })

  const constants = useFieldArray({ control: form.control, name: 'constants' })
  const coefficients = useFieldArray({ control: form.control, name: 'coefficients' })

  const submitForm = async (values: LinearPrice) => {
    await onSubmit(values).then(() => {
      onRegisterSave({
        changes: [],
        save: () => form.handleSubmit(submitForm)(),
      })
    })
  }

  const handleChange = () => {
    const changes = billablePriceToFieldChanges({ before: linearPrice, after: form.getValues() })
    onDataChange?.(form.getValues())
    onRegisterSave({
      changes: changes,
      save: () => form.handleSubmit(submitForm)(),
    })
  }

  if (constants.fields.length === 0 && coefficients.fields.length === 0) {
    return (
      <Typography color="error" variant="p3Regular">
        No price set for this billable
      </Typography>
    )
  }

  return (
    <form onChange={handleChange}>
      <Stack
        sx={{
          gap: 3,
        }}
      >
        <Stack
          sx={{
            gap: 3,
          }}
        >
          {constants.fields.map((constant, index) => (
            <Controller
              control={form.control}
              name={`constants.${index}.value`}
              key={constant.id}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Fixed price"
                  data-testid="constant"
                  type="number"
                  role="textbox"
                  onChange={(event) => {
                    field.onChange(+event.target.value)
                  }}
                  slotProps={{ input: { endAdornment: constant.currency }, htmlInput: { 'aria-label': 'fixed-price' } }}
                />
              )}
            />
          ))}
          {coefficients.fields.map((coefficient, index) => (
            <Controller
              control={form.control}
              name={`coefficients.${index}.value`}
              key={coefficient.id}
              render={({ field }) => (
                <TextField
                  {...field}
                  role="textbox"
                  label={`Price per (${unit})`}
                  type="number"
                  onChange={(event) => field.onChange(+event.target.value)}
                  slotProps={{ input: { endAdornment: coefficient.currency }, htmlInput: { 'aria-label': 'per-unit-price' } }}
                />
              )}
            />
          ))}
        </Stack>
      </Stack>
    </form>
  )
})

interface GetFieldChangesParams {
  before: LinearPrice | undefined
  after: LinearPrice | undefined
}

const billablePriceToFieldChanges = ({ before, after }: GetFieldChangesParams): FieldChange[] => {
  const beforeConstants = before?.constants.map((c) => ({ name: `Fixed price`, value: c.value })) ?? []
  const beforeCoefficients = before?.coefficients.map((c) => ({ name: `Price per unit`, value: c.value })) ?? []

  const afterConstants = after?.constants.map((c) => ({ name: `Fixed price`, value: c.value })) ?? []
  const afterCoefficients = after?.coefficients.map((c) => ({ name: `Price per unit`, value: c.value })) ?? []

  return getChangedFormFields({
    before: normalizeFormData([...beforeConstants, ...beforeCoefficients]),
    after: normalizeFormData([...afterConstants, ...afterCoefficients]),
    compare: (change) => change.before?.value !== change.after?.value,
  })
}
